import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  IndianRupee, Loader2, Plus, Trash2, Pencil, Paperclip,
} from 'lucide-react';
import type { ApiError } from '@pms/shared';
import {
  financeService, type ReimbursementAccount, type ReimbursementCategory,
  type ReimbursementLedgerEntry, type BudgetRow,
} from '@/services/finance.service';
import { Tabs } from '@/components/ui/tabs';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatINR } from '@/lib/currency';
import { formatDateTime } from '@/lib/dateFormat';
import { cn } from '@/lib/utils';
import { ReimbursementCategoryDialog } from './components/ReimbursementCategoryDialog';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

const TABS = [
  { key: 'accounts', label: 'Accounts' },
  { key: 'ledger', label: 'All Ledger' },
  { key: 'categories', label: 'Categories' },
  { key: 'budget', label: 'Budget' },
];

export function BillLinks({ entry }: { entry: ReimbursementLedgerEntry }) {
  const bills = entry.bill_urls ?? [];
  if (bills.length === 0) return <span className="text-muted-foreground">—</span>;

  const download = async (idx: number) => {
    try {
      const url = await financeService.getBillUrl(entry.id, idx);
      const stored = bills[idx] ?? '';
      const dot = stored.lastIndexOf('.');
      const ext = dot >= 0 ? stored.slice(dot) : '';
      const a = document.createElement('a');
      a.href = url;
      a.download = `bill_${idx + 1}${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      toast.error('Bill unavailable');
    }
  };

  return (
    <div className="flex gap-1">
      {bills.map((_, i) => (
        <button key={i} type="button" onClick={() => void download(i)} className="rounded p-1 text-muted-foreground hover:text-primary" title={`Download Bill ${i + 1}`}>
          <Paperclip className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
}

export function ledgerAmountCell(r: ReimbursementLedgerEntry) {
  const credit = r.entry_type === 'credit';
  return <span className={cn('tabular-nums font-medium', credit ? 'text-emerald-600' : 'text-rose-600')}>{credit ? '+' : '-'}{formatINR(r.amount)}</span>;
}

// ─── Create Account Modal ─────────────────────────────────────────────────────────

function CreateAccountModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState('');
  const { data: available = [], isLoading } = useQuery({
    queryKey: ['finance', 'reimbursement', 'available'],
    queryFn: () => financeService.listAvailableEmployees(),
  });

  const mut = useMutation({
    mutationFn: () => financeService.createReimbursementAccount(userId),
    onSuccess: () => {
      toast.success('Account created');
      void queryClient.invalidateQueries({ queryKey: ['finance', 'reimbursement', 'accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['finance', 'reimbursement', 'available'] });
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <DialogTitle>Create Reimbursement Account</DialogTitle>
          <DialogDescription>Select an employee to create their reimbursement wallet</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="ra-emp">Select Employee</Label>
          <Select value={userId || undefined} onValueChange={setUserId} disabled={isLoading}>
            <SelectTrigger id="ra-emp"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent>
              {available.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name}{e.employee_code ? ` (${e.employee_code})` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => userId && mut.mutate()} disabled={!userId || mut.isPending}>
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create Account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Credit Modal ─────────────────────────────────────────────────────────────────

function CreditModal({ account, onClose }: { account: ReimbursementAccount; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { register, handleSubmit, formState: { errors } } = useForm<{ amount: string; remark: string }>({
    defaultValues: { amount: '', remark: '' },
  });
  const mut = useMutation({
    mutationFn: (v: { amount: string; remark: string }) => financeService.creditReimbursement(account.id, { amount: Number(v.amount), remark: v.remark.trim() || null }),
    onSuccess: () => {
      toast.success('Balance credited');
      void queryClient.invalidateQueries({ queryKey: ['finance', 'reimbursement', 'accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['finance', 'reimbursement', 'ledger'] });
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <DialogTitle>Credit Balance</DialogTitle>
          <DialogDescription>{account.employee_name} · Current balance {formatINR(account.balance)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit((v) => mut.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cr-amt">Amount (₹) *</Label>
            <Input id="cr-amt" type="number" min="0.01" step="0.01" {...register('amount', { required: 'Amount is required', validate: (v) => Number(v) > 0 || 'Enter a positive amount' })} />
            {errors.amount && <p className="text-xs text-destructive">{errors.amount.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cr-remark">Remark</Label>
            <Textarea id="cr-remark" {...register('remark')} rows={2} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Credit Amount
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Accounts Tab ───────────────────────────────────────────────────────────────────

function AccountsTab() {
  const [create, setCreate] = useState(false);
  const [creditTarget, setCreditTarget] = useState<ReimbursementAccount | null>(null);
  const { data = [], isLoading, isError } = useQuery({
    queryKey: ['finance', 'reimbursement', 'accounts'],
    queryFn: () => financeService.listReimbursementAccounts(),
  });

  const columns: Column<ReimbursementAccount>[] = [
    { key: 'emp', header: 'Employee', cell: (r) => <span className="font-medium">{r.employee_name}{r.employee_code ? <span className="text-muted-foreground"> ({r.employee_code})</span> : null}</span> },
    { key: 'balance', header: 'Balance', className: 'text-right', cell: (r) => <span className="font-semibold tabular-nums">{formatINR(r.balance)}</span> },
    { key: 'credited', header: 'Total Credited', className: 'text-right', cell: (r) => <span className="tabular-nums text-emerald-600">{formatINR(r.total_credited)}</span> },
    { key: 'debited', header: 'Total Debited', className: 'text-right', cell: (r) => <span className="tabular-nums text-rose-600">{formatINR(r.total_debited)}</span> },
    { key: 'status', header: 'Status', cell: (r) => <Badge variant={r.is_active ? 'success' : 'secondary'}>{r.is_active ? 'Active' : 'Inactive'}</Badge> },
    { key: 'action', header: 'Action', className: 'w-28', cell: (r) => <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => setCreditTarget(r)}><IndianRupee className="h-3.5 w-3.5" /> Credit</Button> },
  ];

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Reimbursement Accounts</h2>
        <Button onClick={() => setCreate(true)}><Plus className="h-4 w-4" /> Create</Button>
      </div>
      <DataTable columns={columns} rows={data} rowKey={(r) => r.id} loading={isLoading} error={isError} empty="No reimbursement accounts yet." />
      {create && <CreateAccountModal onClose={() => setCreate(false)} />}
      {creditTarget && <CreditModal account={creditTarget} onClose={() => setCreditTarget(null)} />}
    </>
  );
}

// ─── All Ledger Tab ───────────────────────────────────────────────────────────────

function AllLedgerTab() {
  const [page, setPage] = useState(1);
  const limit = 15;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['finance', 'reimbursement', 'ledger', { page }],
    queryFn: () => financeService.listAllReimbursementLedger({ page, limit }),
  });

  const columns: Column<ReimbursementLedgerEntry>[] = [
    { key: 'date', header: 'Date', cell: (r) => <span className="whitespace-nowrap text-sm">{formatDateTime(r.created_at)}</span> },
    { key: 'emp', header: 'Employee', cell: (r) => <span className="font-medium">{r.employee_name}</span> },
    { key: 'type', header: 'Type', cell: (r) => <Badge variant={r.entry_type === 'credit' ? 'success' : 'destructive'}>{r.entry_type === 'credit' ? 'Credit' : 'Debit'}</Badge> },
    { key: 'cat', header: 'Category', cell: (r) => r.category_name ?? <span className="text-muted-foreground">—</span> },
    { key: 'desc', header: 'Description', cell: (r) => <span className="block max-w-[180px] truncate text-sm">{r.description ?? '—'}</span> },
    { key: 'amount', header: 'Amount', className: 'text-right', cell: ledgerAmountCell },
    { key: 'balance', header: 'Balance', className: 'text-right', cell: (r) => <span className="font-mono tabular-nums">{formatINR(r.running_balance)}</span> },
    { key: 'bills', header: 'Bills', cell: (r) => <BillLinks entry={r} /> },
  ];

  const pagination = data ? { page: data.pagination.page, totalPages: data.pagination.totalPages, total: data.pagination.total, onPageChange: setPage } : undefined;
  return <DataTable columns={columns} rows={data?.data ?? []} rowKey={(r) => r.id} loading={isLoading} error={isError} empty="No transactions yet." pagination={pagination} />;
}

// ─── Categories Tab ───────────────────────────────────────────────────────────────

function CategoriesTab() {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ mode: 'create' | 'edit'; category?: ReimbursementCategory } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const { data = [], isLoading } = useQuery({
    queryKey: ['finance', 'reimbursement', 'categories'],
    queryFn: () => financeService.listReimbursementCategories(),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => financeService.deleteReimbursementCategory(id),
    onSuccess: () => { toast.success('Category deleted'); void queryClient.invalidateQueries({ queryKey: ['finance', 'reimbursement', 'categories'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const columns: Column<ReimbursementCategory>[] = [
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'month', header: 'Monthly Limit', className: 'text-right', cell: (r) => formatINR(r.monthly_limit) },
    { key: 'claim', header: 'Per Claim Limit', className: 'text-right', cell: (r) => formatINR(r.per_claim_limit) },
    { key: 'bill', header: 'Bill Above', className: 'text-right', cell: (r) => formatINR(r.bill_required_above) },
    { key: 'gst', header: 'GST', cell: (r) => (r.gst_applicable ? 'Yes' : 'No') },
    { key: 'status', header: 'Status', cell: (r) => <Badge variant={r.is_active ? 'success' : 'secondary'}>{r.is_active ? 'Active' : 'Inactive'}</Badge> },
    {
      key: 'actions', header: 'Actions', className: 'w-24',
      cell: (r) => (
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => setDialog({ mode: 'edit', category: r })} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
          <button type="button" onClick={() => setDeleteTarget({ id: r.id, name: r.name })} className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Expense Categories</h2>
        <Button onClick={() => setDialog({ mode: 'create' })}><Plus className="h-4 w-4" /> Add</Button>
      </div>
      <DataTable columns={columns} rows={data} rowKey={(r) => r.id} loading={isLoading} empty="No categories found" />
      {dialog && <ReimbursementCategoryDialog open mode={dialog.mode} category={dialog.category} onClose={() => setDialog(null)} onSuccess={() => setDialog(null)} />}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>Delete &quot;{deleteTarget?.name}&quot;?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) { deleteMut.mutate(deleteTarget.id); setDeleteTarget(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Budget Tab ─────────────────────────────────────────────────────────────────────

function BudgetTab() {
  const { data = [], isLoading } = useQuery({
    queryKey: ['finance', 'reimbursement', 'budget'],
    queryFn: () => financeService.getReimbursementBudget(),
  });
  if (isLoading) return <div className="grid gap-4 sm:grid-cols-2">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-muted/40" />)}</div>;
  if (data.length === 0) return <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">No categories with budget limits configured.</div>;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {(data as BudgetRow[]).map((b) => {
        const over = b.pct >= 100; const warn = b.pct >= 80 && b.pct < 100;
        return (
          <div key={b.id} className={cn('rounded-xl border bg-card p-5', over && 'border-rose-300', warn && 'border-amber-300')}>
            <div className="flex items-center justify-between">
              <p className="font-semibold">{b.name}</p>
              {over && <Badge variant="destructive">Over</Badge>}
              {warn && <Badge variant="warning">Warning</Badge>}
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted">
              <div className={cn('h-full rounded-full', over ? 'bg-rose-500' : warn ? 'bg-amber-500' : 'bg-primary')} style={{ width: `${Math.min(b.pct, 100)}%` }} />
            </div>
            <div className="mt-2 flex justify-between text-xs text-muted-foreground">
              <span>Used: {formatINR(b.used)}</span><span>Limit: {formatINR(b.budget)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────────

export function ReimbursementsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'accounts';
  const setTab = (key: string) => setSearchParams({ tab: key });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Finance Control Panel</h1>
        <p className="text-sm text-muted-foreground">Manage reimbursement accounts, categories, and ledger</p>
      </div>
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      <div className="mt-2">
        {tab === 'accounts' && <AccountsTab />}
        {tab === 'ledger' && <AllLedgerTab />}
        {tab === 'categories' && <CategoriesTab />}
        {tab === 'budget' && <BudgetTab />}
      </div>
    </div>
  );
}
